import { useCallback } from "react";
import { toast } from "sonner";
import { Permission, Resource, PermissionType, Role } from "../types";
import { savePermissions, loadPermissions } from "../permissionService";
import { logPermissionChanges } from "../services/auditLogger";

/**
 * Hook for permission update operations
 */
export const usePermissionUpdater = ({
  user,
  permissions,
  setPermissions,
  setInitialPermissions,
  didSaveRef,
  setLastSaveTime,
  logPermissionsState
}) => {
  
  // Method to save permissions with better error handling and feedback
  const savePermissionsHandler = useCallback(async () => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] [useRolePermissions] Starting save operation`);
    
    try {
      // Check if user has management role
      if (user?.role !== "management") {
        toast.error("Alleen management gebruikers kunnen rechten opslaan");
        return false;
      }

      console.log(`[${timestamp}] [useRolePermissions] Saving permissions, count:`, permissions?.length || 0);
      
      // Mark that we're saving to prevent dirty check from running incorrectly
      didSaveRef.current = true;

      // Snapshot before save for audit comparison
      const beforeSnapshot = structuredClone(permissions);
      
      // Create a deep copy of permissions to save to ensure no reference issues
      const permissionsToSave = structuredClone(permissions);
      
      // Save to both database and localStorage
      const saved = await savePermissions(permissionsToSave);
      
      if (saved) {
        // Log successful save
        console.log(`[${timestamp}] [useRolePermissions] Save successful, updating initialPermissions snapshot`);
        
        // Need to reload the permissions from the database to get the updated UUIDs
        try {
          const updatedPermissions = await loadPermissions();
          if (Array.isArray(updatedPermissions) && updatedPermissions.length > 0) {
            console.log(`[${timestamp}] [useRolePermissions] Setting permissions with reloaded data:`, updatedPermissions.length);
            const newSnapshot = structuredClone(updatedPermissions);
            setPermissions(newSnapshot);
            setInitialPermissions(newSnapshot);
            setLastSaveTime(new Date().toLocaleTimeString());
          }
        } catch (reloadError) {
          console.error(`[${timestamp}] [useRolePermissions] Error reloading permissions after save:`, reloadError);
          // Even if reload fails, the save was successful
          const newSnapshot = structuredClone(permissions);
          setInitialPermissions(newSnapshot);
          setLastSaveTime(new Date().toLocaleTimeString());
        }
        
        // Log success
        console.log(`[${timestamp}] [useRolePermissions] Permissions saved successfully`);

        // Log audit trail
        await logPermissionChanges(
          beforeSnapshot,
          permissions,
          user?.id || "unknown",
          user?.name || "Onbekend"
        );

        toast.success("Rechten succesvol opgeslagen");
        
        // Log the state after save
        setTimeout(() => {
          console.log(`[${timestamp}] [useRolePermissions] State after save:`);
          logPermissionsState();
        }, 100);
        
        return true;
      } else {
        console.error(`[${timestamp}] [useRolePermissions] Save operation failed`);
        toast.error("Er is een fout opgetreden bij het opslaan van de rechten");
        return false;
      }
    } catch (error: any) {
      console.error(`[${timestamp}] [useRolePermissions] Error saving permissions:`, error);
      toast.error(`Fout bij opslaan: ${error?.message || "Onbekende fout"}`);
      return false;
    }
  }, [
    user?.role,
    permissions,
    didSaveRef,
    setPermissions,
    setInitialPermissions,
    setLastSaveTime,
    logPermissionsState
  ]);

  // Update a specific permission (now also matches by company)
  const updatePermission = useCallback((
    role: Role,
    resource: Resource,
    permissionType: PermissionType,
    value: boolean,
    company?: string
  ) => {
    try {
      if (user?.role !== "management") {
        toast.error("Alleen management gebruikers kunnen rechten wijzigen");
        return;
      }

      console.log(`[useRolePermissions] Updating permission: ${role} - ${resource} - ${permissionType} = ${value} (company: ${company || 'all'})`);
      
      setPermissions(prev => {
        // Create a deep copy using structuredClone for reliability
        const updatedPermissions = structuredClone(prev);
        
        // Find and update the specific permission (match by company if provided)
        const permissionIndex = updatedPermissions.findIndex(
          (perm: Permission) => perm.role === role && perm.resource === resource && (!company || perm.company === company)
        );
        
        if (permissionIndex !== -1) {
          console.log("[useRolePermissions] Found permission to update at index:", permissionIndex);
          updatedPermissions[permissionIndex][permissionType] = value;
        } else {
          console.error("[useRolePermissions] Could not find permission to update");
          toast.error(`Kon rechten niet wijzigen voor: ${role} - ${resource}`);
        }
        
        return updatedPermissions;
      });
    } catch (error: any) {
      console.error("[useRolePermissions] Error updating permission:", error);
      toast.error(`Fout bij wijzigen rechten: ${error?.message || "Onbekende fout"}`);
    }
  }, [user?.role, setPermissions]);

  return {
    savePermissions: savePermissionsHandler,
    updatePermission
  };
};
